import Const from 'constants';
import {resolveExposuresOverlap} from "helpers/exposure";
import {getTeamNamesOfGames} from 'helpers/game-helper';
import {generateSignature} from 'helpers/hash';

const defaultTeamState = {
    active: false,
    checked: true,
    exposure: {min: 0, max: 100},
    lineGroups: Const.DEFAULT_VALUES_OF_LINEUPS_NHL_FILTER.lineGroups,
    ppLines: Const.DEFAULT_VALUES_OF_LINEUPS_NHL_FILTER.ppLines,
};

const defaultStackState = {
    exposure: {
        min: 0,
        max: 100,
    },
    checked: true,
    teams: {},
};

const defaultStacksState = {
    [Const.NHL_LEGACY_TEAM_STACKING_FIRST_STACK]: cp(defaultStackState),
    [Const.NHL_LEGACY_TEAM_STACKING_SECOND_STACK]: cp(defaultStackState),
    [Const.NHL_LEGACY_TEAM_STACKING_THIRD_STACK]: cp(defaultStackState),
};

const defaultCombinationsState = {
    '2-2-2': {
        checked: false,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_LEGACY_TEAM_STACKING_FIRST_STACK]: 2,
            [Const.NHL_LEGACY_TEAM_STACKING_SECOND_STACK]: 2,
            [Const.NHL_LEGACY_TEAM_STACKING_THIRD_STACK]: 2,
        },
        isDefensemanMandatory: false,
    },
    '3-2-X': {
        checked: true,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_LEGACY_TEAM_STACKING_FIRST_STACK]: 3,
            [Const.NHL_LEGACY_TEAM_STACKING_SECOND_STACK]: 2,
            [Const.NHL_LEGACY_TEAM_STACKING_THIRD_STACK]: 1,
        },
        isDefensemanMandatory: false,
    },
    '3-2-2': {
        checked: false,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_LEGACY_TEAM_STACKING_FIRST_STACK]: 3,
            [Const.NHL_LEGACY_TEAM_STACKING_SECOND_STACK]: 2,
            [Const.NHL_LEGACY_TEAM_STACKING_THIRD_STACK]: 2,
        },
        isDefensemanMandatory: false,
    },
    '3-3-X': {
        checked: true,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_LEGACY_TEAM_STACKING_FIRST_STACK]: 3,
            [Const.NHL_LEGACY_TEAM_STACKING_SECOND_STACK]: 3,
            [Const.NHL_LEGACY_TEAM_STACKING_THIRD_STACK]: 1,
        },
        isDefensemanMandatory: false,
    },
    '3-3-2': {
        checked: false,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_LEGACY_TEAM_STACKING_FIRST_STACK]: 3,
            [Const.NHL_LEGACY_TEAM_STACKING_SECOND_STACK]: 3,
            [Const.NHL_LEGACY_TEAM_STACKING_THIRD_STACK]: 2,
        },
        isDefensemanMandatory: false,
    },
    '4-2-X': {
        checked: true,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_LEGACY_TEAM_STACKING_FIRST_STACK]: 4,
            [Const.NHL_LEGACY_TEAM_STACKING_SECOND_STACK]: 2,
            [Const.NHL_LEGACY_TEAM_STACKING_THIRD_STACK]: 1,
        },
        isDefensemanMandatory: true,
    },
    '4-2-2': {
        checked: false,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_LEGACY_TEAM_STACKING_FIRST_STACK]: 4,
            [Const.NHL_LEGACY_TEAM_STACKING_SECOND_STACK]: 2,
            [Const.NHL_LEGACY_TEAM_STACKING_THIRD_STACK]: 2,
        },
        isDefensemanMandatory: true,
    },
    '4-3-X': {
        checked: true,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_LEGACY_TEAM_STACKING_FIRST_STACK]: 4,
            [Const.NHL_LEGACY_TEAM_STACKING_SECOND_STACK]: 3,
            [Const.NHL_LEGACY_TEAM_STACKING_THIRD_STACK]: 1,
        },
        isDefensemanMandatory: true,
    },
};

let combinationsState = {...defaultCombinationsState};

const defaultState = {
    enabled: false,
    signature: null,
    data: {
        stacks: cp(defaultStacksState),
        combinations: cp(combinationsState),
        chooseTeamsRandomly: false,
        forceTeamsToStacking: true,
    },
    isEnabled() {
        return this.enabled;
    },
    getStateId() {
        return 'NhlTeamStacking.Settings';
    },
    getStacks() {
        return {...this.data.stacks};
    },
    getCombinations() {
        return {...this.data.combinations};
    },
    getCheckedCombinations() {
        const combos = {};
        Object.keys(this.data.combinations).map(comboKey => {
            const combo = this.data.combinations[comboKey];
            if (combo.checked) {
                combos[comboKey] = {...combo};
            }
        });
        return combos;
    },
    isThereCheckedCombination() {
        for (let combo of Object.values(this.data.combinations)) {
            if (combo.checked === true) {
                return true;
            }
        }
        return false;
    },
    isCombinationChecked(key) {
        return this.data.combinations[key].checked;
    },
    getCombinationExposure(key, exposureType) {
        return this.data.combinations[key].exposure[exposureType];
    },
    getPlayersQuantityByStackNumber(stackNumber) {
        return this.data.stacks[stackNumber].playersQuantity;
    },
    isTeamsChosenRandomly() {
        return Boolean(this.data.chooseTeamsRandomly);
    },
    isTeamsForcedToStacking() {
        return Boolean(this.data.forceTeamsToStacking);
    },
    isAllActiveStackTeamsCheckedByStackNumber(stackNumber) {
        const teams = this.getActiveTeamsByStackNumber(stackNumber);
        for (let team of Object.values(teams)) {
            if (!team.checked) {
                return false;
            }
        }

        return true;
    },
    getStackTeamsExposureByTypeAndStackNumber(exposureType, stackNumber) {
        return this.data.stacks[stackNumber].exposure[exposureType];
    },
    getStackTeamsMinExposureByStackNumber(stackNumber) {
        return this.getStackTeamsExposureByTypeAndStackNumber(
            'min',
            stackNumber
        );
    },
    getStackTeamsMaxExposureByStackNumber(stackNumber) {
        return this.getStackTeamsExposureByTypeAndStackNumber(
            'max',
            stackNumber
        );
    },
    getLineGroupsByStackNumber(stackNumber) {
        const lineGroups = {...Const.DEFAULT_VALUES_OF_LINEUPS_NHL_FILTER.lineGroups};

        Object.keys(lineGroups).map(lineGroupKey => {lineGroups[lineGroupKey] = true});

        const teams = this.data.stacks[stackNumber].teams;

        Object.values(teams).map(team => {
            if (!team.active) {
                return;
            }

            const teamLineGroups = team.lineGroups
            Object.keys(teamLineGroups).map(lineGroupKey => {
                if (teamLineGroups[lineGroupKey] === false) {
                    lineGroups[lineGroupKey] = false;
                }
            })
        })

        return lineGroups;
    },
    getTeamLineGroups(stackNumber, team) {
        return this.data.stacks[stackNumber].teams[team].lineGroups ?? {};
    },
    getPPLinesByStackNumber(stackNumber) {
        const ppLines = {...Const.DEFAULT_VALUES_OF_LINEUPS_NHL_FILTER.ppLines};

        Object.keys(ppLines).map(lineGroupKey => {ppLines[lineGroupKey] = true});

        const teams = this.data.stacks[stackNumber].teams;

        Object.values(teams).map(team => {
            if (!team.active) {
                return;
            }

            const teamPPLines = team.ppLines
            Object.keys(teamPPLines).map(ppLineKey => {
                if (teamPPLines[ppLineKey] === false) {
                    ppLines[ppLineKey] = false;
                }
            })
        })

        return ppLines;
    },
    getTeamPPLines(stackNumber, team) {
        return this.data.stacks[stackNumber].teams[team].ppLines ?? {};
    },
    isTeamCheckedInStackNumber(team, stackNumber) {
        const stack = this.data.stacks[stackNumber];
        if (stack.teams.hasOwnProperty(team) &&
            stack.teams[team].hasOwnProperty('checked')) {
            return stack.teams[team].checked;
        }
        return stack.checked;
    },
    getTeamExposureInStackNumber(team, exposureType, stackNumber) {
        const stack = this.data.stacks[stackNumber];
        if (stack.teams.hasOwnProperty(team) &&
            stack.teams[team].hasOwnProperty('exposure')) {
            return stack.teams[team].exposure[exposureType];
        }
        return stack.exposure[exposureType];
    },
    getTotalMinExposureOfCheckedActiveTeamsInStackNumber(stackNumber) {
        const stack = this.data.stacks[stackNumber];

        let totalMinExposure = 0;
        Object.values(stack.teams).map(teamItem => {
            if (teamItem.checked && teamItem.active) {
                totalMinExposure += teamItem.exposure.min;
            }
        });

        return totalMinExposure;
    },
    getTotalMinExposureOfCheckedStackCombinations() {
        return this.getTotalExposureOfCheckedStackCombinations('min');
    },
    getTotalMaxExposureOfCheckedStackCombinations() {
        return this.getTotalExposureOfCheckedStackCombinations('max');
    },
    getTotalExposureOfCheckedStackCombinations(exposureType) {
        const combinations = this.data.combinations;

        let totalExposure = 0;
        Object.values(combinations).map(stackItem => {
            if (stackItem.checked) {
                totalExposure += stackItem.exposure[exposureType];
            }
        });

        return totalExposure;
    },
    getActiveTeamsByStackNumber(stackNumber) {
        const activeTeams = {};

        if (!this.data.stacks.hasOwnProperty(stackNumber)) {
            return activeTeams;
        }

        const {teams} = this.data.stacks[stackNumber];

        Object.keys(teams).map(teamName => {
            if (teams[teamName].active) {
                activeTeams[teamName] = teams[teamName];
            }
        });

        return activeTeams;
    },
    getActiveAndCheckedTeamsByStackNumber(stackNumber) {
        const activeTeams = {};

        if (!this.data.stacks.hasOwnProperty(stackNumber)) {
            return activeTeams;
        }

        const {teams} = this.data.stacks[stackNumber];

        Object.keys(teams).map(teamName => {
            if (teams[teamName].active && teams[teamName].checked) {
                activeTeams[teamName] = teams[teamName];
            }
        });

        return activeTeams;
    },
    getExposureSumOfCheckedTeamsByStackNumber(stackNumber) {
        const teams = this.getActiveAndCheckedTeamsByStackNumber(stackNumber);
        let exposureSum = 0;
        Object.keys(teams).map(teamName => {
            exposureSum += this.getTeamMaxExposureInStackNumber(teamName, stackNumber);
        });
        return exposureSum;
    },
    getCheckedTeamNamesByStackNumber(stackNumber) {
        const teams = this.getActiveAndCheckedTeamsByStackNumber(stackNumber);

        return Object.keys(teams).map(teamName => teamName);
    },
    getTeamMinExposureInStackNumber(team, stackNumber) {
        return this.getTeamExposureInStackNumber(team, 'min', stackNumber);
    },
    getTeamMaxExposureInStackNumber(team, stackNumber) {
        return this.getTeamExposureInStackNumber(team, 'max', stackNumber);
    },
    getCombinationMinExposureValue(comboKey) {
        return this.getCombinationExposure(comboKey, 'min');
    },
    getCombinationMaxExposureValue(comboKey) {
        return this.getCombinationExposure(comboKey, 'max');
    },
    isDefensemanMandatoryInCombinationStack(combo, stackNumber) {
        const playersQuantityWithMandatoryDefenseman = 4;

        return combo.playersQuantity[stackNumber] >= playersQuantityWithMandatoryDefenseman;
    },
    getSignature() {
        return this.signature;
    }
};

export default function (state = defaultState, action) {

    switch (action.type) {

        case Const.NHL_LEGACY_TEAM_STACKING_IS_USED:

            state = {...state};
            state.enabled = Boolean(action.use);
            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_COMBO_IS_CHECKED:

            state = {...state};
        {
            const combination = state.data.combinations[action.comboKey];
            combination.checked = action.checked;
        }
            return state;


        case Const.NHL_LEGACY_TEAM_STACKING_SET_COMBO_EXPOSURE:

            state = {...state};
            {
                const combination = state.data.combinations[action.comboKey];
                combination.exposure = action.exposure;
            }

            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_TEAMS_ARE_CHOSEN_RANDOMLY:

            state = {...state};
            state.data.chooseTeamsRandomly = Boolean(action.chooseTeamsRandomly);

            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_TEAMS_ARE_FORCED_TO_STACKING:

            state = {...state};
            state.data.forceTeamsToStacking = Boolean(action.forceTeamsToStacking);

            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_TEAMS:

            state = {...state};

            {
                Object.values(state.data.stacks).map(stack => {

                    const teams = {};

                    Object.keys(stack.teams).map(teamName => {
                        teams[teamName] = stack.teams[teamName];
                        teams[teamName].active = inArray(teamName, action.teams);
                    });

                    stack.teams = teams;
                });
            }

            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_STACK_TEAMS_IS_CHECKED:

            state = {...state};
        {
            const stack = state.data.stacks[action.stackNumber];
            stack.checked = action.checked;
            Object.values(stack.teams).map(team => {
                if (team.active) {
                    team.checked = action.checked;
                }
            });
        }
            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_STACK_TEAMS_EXPOSURE:

            state = {...state};
        {
            const stack = state.data.stacks[action.stackNumber];
            Object.keys(action.exposure).map(exposureType => {

                const value = action.exposure[exposureType];
                stack.exposure[exposureType] = value;

                Object.values(stack.teams).map(team => {
                    const currentExposure = {...team.exposure};
                    currentExposure[exposureType] = value;
                    team.exposure = resolveExposuresOverlap(
                        currentExposure,
                        exposureType
                    );
                });

            });
        }
            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_TEAM_LINE_GROUP_IS_CHECKED:

            state = {...state};
        {
            const teams = state.data.stacks[action.stackNumber].teams;
            Object.values(teams).map(team => {
                if (team.active === true) {
                    team.lineGroups = {...team.lineGroups, ...action.lineGroups};
                }
            })
        }
            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_TEAM_LINE_GROUPS:

            state = {...state};
        {
            const team = state.data.stacks[action.stackNumber].teams[action.team];
            team.lineGroups = {...team.lineGroups, ...action.lineGroups};
        }
            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_TEAM_PP_LINE_IS_CHECKED:

            state = {...state};
        {
            const teams = state.data.stacks[action.stackNumber].teams;
            Object.values(teams).map(team => {
                if (team.active === true) {
                    team.ppLines = {...team.ppLines, ...action.ppLines};
                }
            })
        }
            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_TEAM_PP_LINES:

            state = {...state};
        {
            const team = state.data.stacks[action.stackNumber].teams[action.team];
            team.ppLines = {...team.ppLines, ...action.ppLines};
        }
            return state;


        case Const.NHL_LEGACY_TEAM_STACKING_SET_TEAM_IS_CHECKED:

            state = {...state};
        {
            const stack = state.data.stacks[action.stackNumber];
            if (!stack.teams.hasOwnProperty(action.team)) {
                stack.teams[action.team] = {...defaultTeamState};
            }
            stack.teams[action.team].checked = Boolean(action.checked);
        }
            return state;

        case Const.NHL_LEGACY_TEAM_STACKING_SET_TEAM_EXPOSURE:

            state = {...state};
        {
            const stack = state.data.stacks[action.stackNumber];
            if (!stack.teams.hasOwnProperty(action.team)) {
                stack.teams[action.team] = {...defaultTeamState};
            }
            stack.teams[action.team].exposure = action.exposure;
        }
            return state;
        case Const.NHL_LEGACY_TEAM_STACKING_MIRROR_STACK:

            state = {...state};
        {
            state.data.stacks[action.destinationStackNumber] = cp(state.data.stacks[action.sourceStackNumber]);
        }
            return state;
        case Const.NHL_LEGACY_TEAM_STACKING_SET_DATA_FROM_STORAGE:

            state = {...state};

            {
                state.data = cp(action.data);
            }

            return state;

        case Const.GET_GAMES_SUCCESS:

            state = {...state};

            {
                const teamNames = getTeamNamesOfGames(action.data);

                Object.values(state.data.stacks).map(stack => {

                    const teams = {};
                    
                    teamNames.map(teamName => {
                        if (stack.teams.hasOwnProperty(teamName)) {
                            teams[teamName] = stack.teams[teamName];
                        } else {
                            teams[teamName] = cp(defaultTeamState);
                        }
                    });

                    stack.teams = teams;
                });
            }

            return state;

        case Const.USE_HITTERS_STACK:

            if (action.data) {
                state = {...state};
                state.enabled = false;
            }

            return state;

        case Const.FILTER_BY_SITE:

            combinationsState = {...defaultCombinationsState};

            return {
                ...defaultState,
                data: {
                    stacks: cp(defaultStacksState),
                    combinations: cp(combinationsState),
                    chooseTeamsRandomly: false,
                    forceTeamsToStacking: true,
                },
                enabled: false,
                signature: generateSignature(),
            };

        case Const.RESET_ALL_SETTINGS:

            const {stacks} = state.data;
            const stacksState = cp(defaultStacksState);

            Object.keys(stacks).map(stackGroup => {
                Object.keys(stacks[stackGroup].teams).map(teamName => {
                    stacksState[stackGroup].teams[teamName] = {...defaultTeamState, active: true};
                });
            });

            return {
                ...defaultState,
                data: {
                    stacks: stacksState,
                    combinations: cp(combinationsState),
                    chooseTeamsRandomly: false,
                    forceTeamsToStacking: true,
                },
                enabled: false,
                signature: generateSignature(),
            };

        case Const.FILTER_BY_SLATE:

            return {
                ...defaultState,
                data: {
                    stacks: cp(defaultStacksState),
                    combinations: cp(combinationsState),
                    chooseTeamsRandomly: false,
                    forceTeamsToStacking: true,
                },
                enabled: false,
                signature: generateSignature(),
            };

        case Const.NHL_TEAM_STACKING_IS_USED:
            state = {...state};
            if (Boolean(action.use)) {
                state.enabled = false;
            }
            return state;

        default:
            return state;
    }
}


